import React, { useState, useEffect } from 'react';
import { fetchPolygonSnapshotPrice } from '../../composables/fetchPolygon';
import toast from 'react-hot-toast';
import { getProgressBarColor } from '../../composables/colorUtils';
import { useExchangeRates } from '../../context/ExchangeRateContext';
import EditHoldingAllocationModal from './EditHoldingAllocationModal';
import { useNavigate, useParams } from 'react-router-dom'; // ✅ 1. Import useNavigate เข้ามา
import EditRealizeGainModal from './EditRealizeGainModal'; // ✅ Import Modal ใหม่
import AddEditStock from '../AddEditStock';
import { usePortfolio } from '../../context/PortfolioContext';
import EditDividendModal from './EditDividendModal';
import { updateStockInSubPortfolioTwo } from '../../composables/fetchAPI';
import SellPartialStockModal from './SellPartialStockModal'; // ปรับ path ตามโฟลเดอร์จริง
import { formatCryptoQty, formatCryptoUsd, num } from '../../utils/cryptoDecimal';

function getStockProfitInfo(stock: any, currentPrice: number | undefined, exchangeRate: number) {
  const avgPrice = stock.avgBuyPrice || stock.avgEntryPrice || 0;
  if (!currentPrice) return { profit: 0, profitPercent: 0 };
  const profit = (currentPrice - avgPrice) * stock.quantity * exchangeRate;
  const cost = avgPrice * stock.quantity * exchangeRate;
  const profitPercent = cost ? (profit / cost) * 100 : 0;
  return { profit, profitPercent };
}

// % ของสัดส่วนปัจจุบันเทียบกับเป้าหมาย — ถ้าเป้าหมายเป็น 0 แต่ยังถือหุ้นอยู่ ถือว่าเกินเป้า
function getTargetProgress(currentRatio: number, expectedRatio: number | undefined) {
  const target = Number(expectedRatio) || 0;
  if (target <= 0) return currentRatio > 0 ? 101 : 0;
  return (currentRatio / target) * 100;
}

function getPortfolioValue(portfolio: any, currentPrices: { [symbol: string]: number }, exchangeRate: number) {
  if (!portfolio.stocks) return 0;
  return portfolio.stocks.reduce((sum: number, stock: any) => {
    const price = currentPrices[stock.stockSymbol] ?? 0;
    return sum + stock.quantity * price * exchangeRate;
  }, 0);
}
const PortfolioStocksBox: React.FC<{
  portfolio: any;
  onBack: () => void;
  onDeleteStock?: (symbol: string) => void;
  onEditStock?: (stock: any) => void;
  onUpdatePortfolioSummary?: (summary: { totalValue: number; totalProfit: number }) => void;
  onRefresh?: () => void;
  isPortAdmin?: boolean;
  onSelectStock?: (symbol: any) => void;
  onMoveStock: (stocks: any[]) => void;
  currency?: string;
  currentPrices: { [symbol: string]: number };
  convertCurrency?: (value: number, currency: string) => number;
  portfolioId?: string;
  setIsSellBuyModalOpen: (isOpen: boolean) => void;
  setIsImportModalOpen: (isOpen: boolean) => void;
  onNavigateToPendingDividend?: (symbol: string) => void;
  onNavigateToSettledDividend?: (symbol: string) => void;
  fromShayAdmin?: boolean;
  isCrypto?: boolean; // พอร์ตคริปโต: ไม่มีปันผล, จำนวนเหรียญเป็นทศนิยมยาว
}> = ({ portfolio, onBack, onDeleteStock, onEditStock, isPortAdmin, onSelectStock, onMoveStock, currency, convertCurrency, currentPrices, onRefresh, portfolioId, setIsSellBuyModalOpen, setIsImportModalOpen, onNavigateToPendingDividend, onNavigateToSettledDividend, fromShayAdmin, isCrypto = false }) => {
  const unitLabel = isCrypto ? 'เหรียญ' : 'หุ้น';
  // ยอดเงินในตาราง: คริปโตไม่ปัดยอดเล็กมากเป็น 0.00
  const money = (v: number, opts: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 2 }) =>
    (isCrypto ? formatCryptoUsd(v) : v.toLocaleString(undefined, opts));
  const qtyText = (q: any) => (isCrypto ? formatCryptoQty(q) : q);
  const symbolText = (stock: any) => (isCrypto ? (stock.displaySymbol || stock.assetSymbol) : (stock.stockSymbol || stock.assetSymbol || stock.symbol));
  const { openTransactionModal, displayMode } = usePortfolio();
  const isV2 = displayMode === 'V2';
  const { id } = useParams();
  const STORAGE_KEY_SORT = 'portfolio_sort_config';
  // ✅ 1. State สำหรับแถวที่ถูกกางออก (Expanded Row)
  const [expandedSymbol, setExpandedSymbol] = useState<string | null>(null);
  // ✅ 2. State สำหรับ Modal แก้ไข Realize Gain
  const [isEditGainOpen, setIsEditGainOpen] = useState(false);
  const [isEditDividendOpen, setIsEditDividendOpen] = useState(false);

  const [selectedStockForGain, setSelectedStockForGain] = useState<any>(null);
  const [hideClosedStocks, setHideClosedStocks] = useState<boolean>(true);
  // ✅ 1. เพิ่ม State สำหรับจัดการเปิด/ปิด Menu 3 จุด
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const navigate = useNavigate(); // ✅ 2. เรียกใช้งาน useNavigate
  const [sortKey, setSortKey] = React.useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SORT);
    return saved ? JSON.parse(saved).key : 'value';
  });
  const [isEditStockOpen, setIsEditStockOpen] = useState(false);
  const [isEditTargetOpen, setIsEditTargetOpen] = useState(false);

  const [selectedStock, setSelectedStock] = useState<any>(null);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_SORT);
    return saved ? JSON.parse(saved).order : 'desc';
  });

  const { exchangeRates } = useExchangeRates();
  const exchangeRate = currency === 'THB' ? exchangeRates['THB'] || 1 : 1;
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  const subPortfolioValue = React.useMemo(() => {
    return getPortfolioValue(portfolio, currentPrices, exchangeRate);
  }, [portfolio, currentPrices, exchangeRate]);

  const [selectedStocks, setSelectedStocks] = useState<string[]>([]); // เก็บ stockSymbol
  const [isBulkMoveMode, setIsBulkMoveMode] = useState(false);

  const [isPartialSellOpen, setIsPartialSellOpen] = useState(false);
  const [stockForPartialSell, setStockForPartialSell] = useState<any>(null);

  const [isSellAllMode, setIsSellAllMode] = useState(false);


  const handleSellAllClick = (stock: any) => {
    toast((t) => (
      <div>
        <p className="text-sm">หากขายทั้งหมด หุ้น <strong>{stock.stockSymbol}</strong> จะมีมูลค่าเป็น 0 แต่ระบบจะยังคงเก็บ กำไร "ล๊อคแล้ว" (Realized Gain) เอาไว้ ใช่หรือไม่?</p>
        <div className="flex justify-end gap-2 mt-3">
          <button
            className="bg-orange-500 text-white px-3 py-1.5 rounded text-sm"
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                // เรียก API เพื่ออัปเดตข้อมูลเป็น 0
                await updateStockInSubPortfolioTwo(
                  portfolioId || portfolio.portId || id || '',
                  portfolio.subPortId || '',
                  stock.stockSymbol,
                  {
                    stockSymbol: stock.stockSymbol,
                    quantity: 0,
                    avgBuyPrice: 0,
                    expectedRatio: 0
                  }
                );
                toast.success('ขายหุ้นทั้งหมดสำเร็จ');
                if (onRefresh) onRefresh();
              } catch (error) {
                toast.error('ไม่สามารถดำเนินการได้');
              }
            }}
          >
            ยืนยัน
          </button>
          <button
            className="bg-gray-300 px-3 py-1.5 rounded text-black text-sm"
            onClick={() => toast.dismiss(t.id)}
          >
            ยกเลิก
          </button>
        </div>
      </div>
    ));
  };
  // ฟังก์ชัน Toggle เลือกหุ้น
  const toggleStockSelection = (symbol: string) => {
    setSelectedStocks(prev =>
      prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol]
    );
  };
  const [isConfigPopupOpen, setIsConfigPopupOpen] = useState(false);
  // const API_URL = process.env.REACT_APP_API_URL;
  // const API_URL = "https://wethaiinvest.api-playground.memolab.me";
  const API_URL = process.env.REACT_APP_API_URL;

  React.useEffect(() => {
    const sortConfig = {
      key: sortKey,
      order: sortOrder
    };
    localStorage.setItem(STORAGE_KEY_SORT, JSON.stringify(sortConfig));
  }, [sortKey, sortOrder]);

  // ✅ 2. เพิ่มระบบคลิกที่อื่นเพื่อปิด Menu (Click Outside)
  useEffect(() => {
    const handleClickOutside = () => setActiveMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleMenu = (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation(); // กันไม่ให้ไปติด onClick ของแถวที่คลุมอยู่
    setActiveMenu(activeMenu === symbol ? null : symbol);
  };

  const handleDeleteClick = (symbol: string) => {
    toast((t) => (
      <div>
        <p>คุณต้องการลบหุ้น <strong>{symbol}</strong> ใช่ไหม?</p>
        <div className="flex justify-end gap-2 mt-2">
          <button
            className="bg-red-500 text-white px-4 py-2 rounded"
            onClick={() => {
              toast.dismiss(t.id);
              if (onDeleteStock) onDeleteStock(symbol);
            }}
          >
            ลบ
          </button>
          <button
            className="bg-gray-300 px-4 py-2 rounded text-black"
            onClick={() => toast.dismiss(t.id)}
          >
            ยกเลิก
          </button>
        </div>
      </div>
    ));
  };
  // ✅ 3. ฟังก์ชันสลับการกาง/หุบ แถวตาราง
  const toggleRowExpand = (symbol: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedSymbol(expandedSymbol === symbol ? null : symbol);
  };
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('desc');
    }
  };
  const sortedStocks = React.useMemo(() => {
    // 1. ตรวจสอบว่า portfolio และ portfolio.stocks มีจริงหรือไม่ก่อนเริ่ม
    if (!portfolio?.stocks) return [];

    return portfolio.stocks
      .filter((stock: any) => {
        // 2. เช็คว่า stockSymbol มีค่าหรือไม่ และจัดการกรณีที่เป็น undefined/null
        const symbol = stock?.stockSymbol || '';
        return symbol.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .filter((stock: any) => hideClosedStocks ? stock?.status !== 'CLOSED' : true)
      .slice()
      .sort((a: any, b: any) => {
        let valueA, valueB;
        switch (sortKey) {
          case 'symbol':
            valueA = a?.stockSymbol?.toLowerCase() || '';
            valueB = b?.stockSymbol?.toLowerCase() || '';
            return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
          case 'avgBuyPrice':
            valueA = a.avgBuyPrice * exchangeRate;
            valueB = b.avgBuyPrice * exchangeRate;
            break;
          case 'cost':
            valueA = (a.costValue ?? (a.quantity * (a.avgEntryPrice || a.avgBuyPrice || 0))) * exchangeRate;
            valueB = (b.costValue ?? (b.quantity * (b.avgEntryPrice || b.avgBuyPrice || 0))) * exchangeRate;
            break;
          case 'value':
            valueA = a.quantity * (currentPrices[a.stockSymbol] ?? 0) * exchangeRate;
            valueB = b.quantity * (currentPrices[b.stockSymbol] ?? 0) * exchangeRate;
            break;
          case 'profit':
            valueA = getStockProfitInfo(a, currentPrices[a.stockSymbol], exchangeRate).profit;
            valueB = getStockProfitInfo(b, currentPrices[b.stockSymbol], exchangeRate).profit;
            break;
          case 'ratio':
            valueA = subPortfolioValue
              ? (a.quantity * (currentPrices[a.stockSymbol] ?? 0) * exchangeRate) / subPortfolioValue
              : 0;
            valueB = subPortfolioValue
              ? (b.quantity * (currentPrices[b.stockSymbol] ?? 0) * exchangeRate) / subPortfolioValue
              : 0;
            break;
          default:
            return 0;
        }

        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      });
  }, [portfolio.stocks, currentPrices, sortKey, sortOrder, subPortfolioValue, searchTerm, hideClosedStocks]);
  return (
    <div className="rounded-b-xl shadow-lg p-4 md:p-6 h-full flex flex-col w-full"
      style={{ background: 'var(--component-bg-color)' }}>
      <div className="flex items-center gap-3 w-full mb-6">

        <button
          onClick={onBack}
          className="px-4 py-2 rounded hover:bg-gray-600 transition-colors flex items-center gap-2 whitespace-nowrap"
          style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
        >
          &larr; <span className="hidden sm:inline">ย้อนกลับ</span>
          <span className="sm:hidden">กลับ</span>
        </button>

        <div className="relative flex-1">
          <input
            type="text"
            placeholder="ค้นหาสัญลักษณ์..."
            className="w-full bg-gray-800 text-sm text-white border border-gray-700 rounded-lg px-4 py-2 outline-none focus:border-yellow-500 transition-colors uppercase"
            style={{ color: 'var(--text-color)', backgroundColor: 'var(--component-bg-color)' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">

        <h2 className="text-lg font-bold" style={{ color: "var(--text-color)" }}>
          {portfolio.name} <span className="font-normal" style={{ color: 'var(--yellow-text-color)' }}>({unitLabel}ในพอร์ต)</span>
        </h2>
        {!isPortAdmin && !fromShayAdmin && (
          <>
            {isV2 && (
              <>

                <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-400 hover:text-white transition-colors w-fit">
                  <input
                    type="checkbox"
                    checked={hideClosedStocks}
                    onChange={(e) => setHideClosedStocks(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-yellow-500 rounded border-gray-600 bg-gray-700"
                  />
                  ซ่อน{unitLabel}ที่ปิดสถานะแล้ว (Closed)
                </label>


                <div className="flex gap-2">
                  <button
                    onClick={() => setIsBulkMoveMode(!isBulkMoveMode)}
                    className="text-xs px-3 py-1.5 bg-gray-700 rounded hover:bg-gray-600"
                  >
                    {isBulkMoveMode ? 'ยกเลิกย้าย' : `ย้าย${unitLabel}`}
                  </button>
                  {isBulkMoveMode && selectedStocks.length > 0 && (
                    <button
                      onClick={() => {
                        // แทนที่จะส่ง [selectedStocks] ให้ส่ง Array ของ Object โดยตรง
                        const itemsToMove = isBulkMoveMode
                          ? portfolio.stocks.filter((s: any) => selectedStocks.includes(s.stockSymbol))
                          : [portfolio.stocks.find((s: any) => selectedStocks.includes(s.stockSymbol))]; // ถ้าเลือกตัวเดียวก็ยังต้องอยู่ใน []

                        onMoveStock(itemsToMove); // ส่งไปเป็น Array ของ Objects
                        setActiveMenu(null);
                      }}
                      className="text-xs px-3 py-1.5 bg-yellow-500 text-black font-bold rounded"
                    >
                      ย้าย ({selectedStocks.length})
                    </button>
                  )}
                </div>
              </>
            )}
            <button
              onClick={() => setIsEditTargetOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-transform active:scale-95 shadow-sm"
              style={{ backgroundColor: 'var(--button-edit-bg)', color: 'var(--text-black)' }}
            >
              🎯 แก้ไขสัดส่วน{unitLabel}
            </button>
          </>)}
      </div>


      {portfolio.stocks && portfolio.stocks.length > 0 ? (
        <div className="w-full">
          {/* --- DESKTOP TABLE --- */}
          <div className="hidden md:block overflow-x-auto pb-32">
            <table className="w-full text-left">
              <thead>

                <tr className="text-gray-400 text-sm border-b border-gray-700" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th className="py-2 px-2 font-semibold cursor-pointer w-8 text-center"></th> {/* ช่องสำหรับลูกศร Toggle */}
                  <th className="py-2 px-2 font-semibold cursor-pointer" style={{ color: 'var(--text-color)' }} onClick={() => handleSort('symbol')}>
                    สัญลักษณ์ {sortKey === 'symbol' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="py-2 px-2 font-semibold cursor-pointer" style={{ color: 'var(--text-color)' }} onClick={() => handleSort('avgBuyPrice')}>
                    ค่าเฉลี่ย {sortKey === 'avgBuyPrice' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="py-2 px-2 font-semibold cursor-pointer" style={{ color: 'var(--text-color)' }} onClick={() => handleSort('cost')}>
                    ต้นทุน {sortKey === 'cost' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="py-2 px-2 font-semibold cursor-pointer" style={{ color: 'var(--text-color)' }} onClick={() => handleSort('value')}>
                    มูลค่า {sortKey === 'value' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="py-2 px-2 font-semibold cursor-pointer" style={{ color: 'var(--text-color)' }} onClick={() => handleSort('profit')}>
                    กำไร/ขาดทุน {sortKey === 'profit' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th className="py-2 px-2 font-semibold cursor-pointer" style={{ color: 'var(--text-color)' }} onClick={() => handleSort('ratio')}>
                    สัดส่วน {sortKey === 'ratio' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  {!isPortAdmin && <th className="py-2 px-2 font-semibold text-center" style={{ color: 'var(--text-color)' }}>จัดการ</th>}
                </tr>
              </thead>
              <tbody>
                {sortedStocks.map((stock: any, idx: number) => {
                  const currentPrice = currentPrices[stock.stockSymbol];
                  const { profit, profitPercent } = getStockProfitInfo(stock, currentPrice, exchangeRate);
                  const stockValue = stock.quantity * (currentPrices[stock.stockSymbol] ?? 0) * exchangeRate;
                  const currentRatio = subPortfolioValue ? (stockValue / subPortfolioValue) * 100 : 0;
                  const avgPriceForCost = stock.avgEntryPrice || stock.avgBuyPrice || 0;
                  const costValueUSD = stock.costValue ?? (stock.quantity * avgPriceForCost);
                  const costValueConverted = costValueUSD * exchangeRate;

                  const isExpanded = expandedSymbol === stock.stockSymbol;

                  return (
                    <React.Fragment key={idx}>
                      <tr
                        className={`border-b border-gray-800 hover:cursor-pointer transition-colors duration-200 ${isExpanded ? 'bg-gray-800/40' : ''} ${activeMenu === stock.stockSymbol ? 'relative z-[99]' : 'relative z-0'}`}
                        style={{ backgroundColor: isExpanded ? 'var(--bg-item-active)' : 'var(--hover-item)' }}
                        onClick={(e) => {

                          toggleRowExpand(stock.stockSymbol, e);
                        }}>

                        <td className="py-3 px-2 text-center text-gray-500">
                          <svg className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </td>

                        <td className="py-3 px-2 font-bold text-base" style={{ color: 'var(--text-color)' }}>

                          {/* ✅ เพิ่มกล่อง flex และ img ตรงนี้ */}
                          <div className="flex items-center gap-3">
                            {isBulkMoveMode && (
                              <td className="py-3 px-2">
                                <input
                                  type="checkbox"
                                  checked={selectedStocks.includes(stock.stockSymbol)}
                                  onClick={(e) => e.stopPropagation()} // 👈 เพิ่มบรรทัดนี้: หยุดไม่ให้คลิก Checkbox แล้วส่งผลต่อแถว
                                  onChange={(e) => {
                                    e.stopPropagation(); // 👈 กันไว้เพิ่มอีกชั้น
                                    toggleStockSelection(stock.stockSymbol);
                                  }}
                                  className="cursor-pointer"
                                />
                              </td>
                            )}


                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center bg-white shadow-sm overflow-hidden relative"
                            >
                              {isCrypto && !stock.logoUrl ? (
                                <span className="text-[10px] font-black text-orange-500">{String(symbolText(stock)).slice(0, 3)}</span>
                              ) : <img
                                src={stock.logoUrl ? `${API_URL}${stock.logoUrl}` : `${API_URL}/stocks/v1/assets/${stock.stockSymbol}/logo`}
                                alt={stock.stockSymbol}
                                className="w-full h-full object-contain "
                                style={{
                                  // เงาจะตกกระทบแค่ที่ตัวรูปโลโก้เท่านั้น
                                  filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))'
                                }}
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              />}

                            </div>
                            <div>
                              {symbolText(stock)}
                              <div className="text-xs font-normal break-all" style={{ color: 'var(--yellow-text-color)' }}>{qtyText(stock.quantity)} {unitLabel}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 font-semibold" style={{ color: 'var(--yellow-text-color)' }}>{currency === 'THB' ? '฿' : '$'}{isCrypto ? formatCryptoUsd(num(stock.avgBuyPrice)) : (stock.avgBuyPrice * exchangeRate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-3 px-2 font-semibold" style={{ color: 'var(--text-color)' }}>{currency === 'THB' ? '฿' : '$'}{money(costValueConverted)}</td>
                        <td className="py-3 px-2 font-semibold" style={{ color: 'var(--text-color)' }}>{currency === 'THB' ? '฿' : '$'}{money(stockValue)}</td>
                        <td className="py-3 px-2">
                          <div className={`font-semibold ${profit > 0 ? 'text-green-500' : profit < 0 ? 'text-red-500' : 'var(--text-color)'}`}>
                            {profit > 0 ? '+' : ''}{currency === 'THB' ? '฿' : '$'}{money(profit)}
                          </div>
                          <div className={`text-xs ${profitPercent > 0 ? 'text-green-500' : profitPercent < 0 ? 'text-red-500' : 'var(--text-color)'}`}>({profitPercent > 0 ? '+' : ''}{profitPercent.toFixed(2)}%)</div>
                        </td>
                        <td className="py-3 px-2">
                          <span className="font-bold" style={{
                            color: fromShayAdmin ? 'var(--text-color)' : getProgressBarColor(getTargetProgress(currentRatio, stock.expectedRatio), stock.expectedRatio)
                          }}>
                            {currentRatio.toFixed(2)}%
                          </span>
                          {!fromShayAdmin && (
                            <div
                              className="h-2 rounded-full w-full relative mt-1"
                              style={{
                                backgroundColor: '#ffffff',
                                maxWidth: '80px',
                              }}
                            >
                              <div
                                className={`h-2 rounded-full`}
                                style={{
                                  width: `${Math.min(getTargetProgress(currentRatio, stock.expectedRatio), 100)}%`,
                                  maxWidth: '100%',
                                  backgroundColor: getProgressBarColor(getTargetProgress(currentRatio, stock.expectedRatio), stock.expectedRatio || 0),
                                }}
                              ></div>
                            </div>
                          )}
                          {!fromShayAdmin && stock.expectedRatio !== undefined && (
                            <div className="text-[12px]" style={{ color: 'var(--text-color)' }}>เป้าหมาย: {stock.expectedRatio}%</div>
                          )}
                        </td>

                        {!isPortAdmin && (
                          <td className="py-3 px-2 whitespace-nowrap text-center relative">
                            <button onClick={(e) => toggleMenu(stock.stockSymbol, e)} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-colors focus:outline-none">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" /></svg>
                            </button>
                            {activeMenu === stock.stockSymbol && (
                              <div className="absolute right-6 top-10 w-32 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-[100] flex flex-col overflow-hidden text-left" onClick={(e) => e.stopPropagation()}>
                                <button onClick={(e) => { e.stopPropagation(); onEditStock?.(stock); setSelectedStock(stock); setActiveMenu(null); setIsEditStockOpen(true); }} className="px-4 py-2.5 text-sm font-bold text-blue-400 hover:bg-gray-700 text-left border-b border-gray-700/50">แก้ไข</button>
                                <button onClick={(e) => { e.stopPropagation(); onMoveStock?.(stock); setActiveMenu(null); }} className="px-4 py-2.5 text-sm font-bold text-yellow-400 hover:bg-gray-700 text-left border-b border-gray-700/50">ย้าย</button>
                                {displayMode === 'V1' && (
                                  <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(stock.stockSymbol); setActiveMenu(null); }} className="px-4 py-2.5 text-sm font-bold text-red-400 hover:bg-gray-700 text-left border-b border-gray-700/50">ลบ</button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (displayMode === 'V1') {
                                      setStockForPartialSell(stock);
                                      setIsSellAllMode(false);
                                      setIsPartialSellOpen(true);
                                      setActiveMenu(null);
                                    } else {
                                      openTransactionModal({
                                        portId: id || portfolioId,
                                        subPortId: portfolio.subPortId,
                                        symbol: stock.stockSymbol,

                                      });
                                    }

                                  }}
                                  className="px-4 py-2.5 text-sm font-bold text-purple-400 hover:bg-gray-700 text-left border-b border-gray-700/50"
                                >
                                  ขายบางส่วน
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (displayMode === 'V1') {
                                      setStockForPartialSell(stock);
                                      setIsSellAllMode(true);   // ✅ เปิดโหมดขายทั้งหมด
                                      setIsPartialSellOpen(true);
                                      setActiveMenu(null);
                                    } else {
                                      // สำหรับ V2: เปิด Modal ซื้อขาย พร้อมพ่วงจำนวนหุ้นทั้งหมดไปเป็นค่าตั้งต้น
                                      // (ถ้าคุณอยากให้ผู้ใช้กรอกจำนวนเองด้วย ก็ไม่ต้องส่ง quantity ไปครับ แต่ตามโจทย์คือส่งจำนวนหุ้นทั้งหมดมา)
                                      openTransactionModal({
                                        portId: id || portfolioId,
                                        subPortId: portfolio.subPortId,
                                        symbol: stock.stockSymbol,
                                        initialQuantity: stock.quantity, // ✅ ดึงจำนวนหุ้นทั้งหมดที่มีโยนเข้าไป
                                        isSellAll: true
                                        // หมายเหตุ: หากต้องการส่งจำนวนหุ้นเริ่มต้นไป ให้ตรวจสอบว่า openTransactionModal รองรับ parameter เพิ่มเติมไหม 
                                        // หรือถ้าต้องการให้กรอกเองทั้งหมดตามโจทย์ สามารถใช้รูปแบบเดียวกับขายบางส่วนได้เลยครับ
                                      });
                                    }

                                  }}
                                  className="px-4 py-2.5 text-sm font-bold ๓ text-orange-400 hover:bg-gray-700 text-left border-b border-gray-700/50"
                                >
                                  ขายทั้งหมด
                                </button>


                                <button onClick={(e) => { e.stopPropagation(); setActiveMenu(null); localStorage.setItem('selectedSymbol', stock.stockSymbol); window.location.href = '/'; }} className="px-4 py-2.5 text-sm font-bold text-green-400 hover:bg-gray-700 text-left">📈 ไปยังกราฟ</button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>

                      {/* ✅ 4. ข้อมูลเพิ่มเติมที่กางออกมา */}
                      {isExpanded && (
                        <tr className="bg-black/20 border-b border-gray-800">
                          <td colSpan={8} className="px-6 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 animate-fadeIn">

                              {/* พอร์ตคริปโตไม่มีปันผล */}
                              {!isCrypto && (<>
                              <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700/50">
                                <div className="text-xs text-gray-400 mb-1">
                                  เงินปันผลสุทธิ (Settled)
                                  {!isPortAdmin && displayMode === 'V1' && (
                                    <button
                                      onClick={() => { setSelectedStockForGain(stock); setIsEditDividendOpen(true); }}
                                      className="text-[10px] bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white px-2 py-0.5 rounded transition-colors"
                                    >
                                      แก้ไข
                                    </button>
                                  )}
                                </div>
                                <div className="font-bold text-blue-400">$
                                  {Number(stock.settledDividendNet ?? stock.manualDividend ?? 0).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}</div>
                              </div>

                              <div
                                className={` bg-gray-800/50 p-3 rounded-lg border border-gray-700/50 transition-all duration-300 
    ${displayMode === 'V2' && !isPortAdmin
                                    ? 'cursor-pointer hover:scale-[1.05] hover:bg-gray-700 hover:border-yellow-500/50 hover:shadow-[0_0_15px_rgba(234,179,8,0.3)]'
                                    : 'bg-gray-800/50'
                                  }`}
                                onClick={() => {
                                  // เช็คเงื่อนไข displayMode === 'V2' ก่อนสั่งงาน
                                  if (displayMode === 'V2' && !isPortAdmin) {
                                    onNavigateToPendingDividend?.(stock.stockSymbol);
                                  }
                                }}
                              >
                                <div className="text-xs text-gray-400 mb-1">เงินปันผลรอดำเนินการ</div>
                                <div className={`font-bold ${displayMode === 'V2' ? 'text-yellow-400' : 'text-yellow-500'}`}>
                                  ${stock.pendingDividendNet?.toLocaleString() || ' -'}
                                </div>

                                {/* เพิ่ม Indicator เล็กๆ ให้รู้ว่ากดได้ถ้าเป็น V2 */}
                                {displayMode === 'V2' && !isPortAdmin && (
                                  <div className="text-[9px] text-yellow-500/70 mt-1 uppercase font-bold tracking-wider">
                                    คลิกเพื่อดูรายการ
                                  </div>
                                )}
                              </div>
                              </>)}
                              {/* <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700/50">
                                <div className="text-xs text-gray-400 mb-1">ค่าธรรมเนียมรวม</div>
                                <div className="font-bold text-orange-400">${stock.feeTotal?.toLocaleString() || ' -'}</div>
                              </div> */}

                              <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-700/50 relative group">
                                <div className="flex justify-between items-start mb-1">
                                  <div className="text-xs text-gray-400">{isCrypto ? 'กำไรที่ขายแล้ว' : 'กำไรจริง (รวมปันผล)'}</div>
                                  {displayMode === 'V2' && !isCrypto && (
                                      <button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (onNavigateToSettledDividend) {
                                            onNavigateToSettledDividend(stock.stockSymbol);
                                          }
                                        }}
                                        className="text-[9px] font-bold bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500 hover:text-black border border-yellow-500/30 px-2 py-0.5 rounded transition-colors"
                                      >
                                        รอลงทุน
                                      </button>
                                    )}
                                  {!isPortAdmin && !isCrypto && (
                                    <button
                                      onClick={() => { setSelectedStockForGain(stock); setIsEditGainOpen(true); }}
                                      className="text-[10px] bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white px-2 py-0.5 rounded transition-colors"
                                    >
                                      แก้ไข
                                    </button>
                                  )}
                                </div>
                                {(() => {
                                 // ✅ เอา realizedGain บวกกับ ปันผลที่ settled แล้ว
                                  const baseRealized = Number(stock.realizedGain ?? stock.manualRealizedGain ?? 0);
                                  const settledDiv = Number(stock.settledDividendNet ?? stock.manualDividend ?? 0);
                                  const realizedVal = baseRealized + settledDiv;
                                  
                                  const isPositive = realizedVal >= 0;
                                  return (
                                    <div className={`font-bold ${isPositive ? 'text-green-400' : 'text-red-500'}`}>
                                      {/* คริปโต: แสดง +/- นำหน้าด้วย (0 ไม่มีเครื่องหมาย) */}
                                      {realizedVal < 0 ? '-' : isCrypto && realizedVal > 0 ? '+' : ''}${money(Math.abs(realizedVal))}
                                    </div>
                                  );
                                })()}
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* --- MOBILE CARDS --- */}
          <div className="md:hidden space-y-3 pb-32">
            {sortedStocks.map((stock: any, idx: number) => {
              const currentPrice = currentPrices[stock.stockSymbol];
              const { profit, profitPercent } = getStockProfitInfo(stock, currentPrice, exchangeRate);
              const stockValue = stock.quantity * (currentPrices[stock.stockSymbol] ?? 0) * exchangeRate;
              const currentRatio = subPortfolioValue ? (stockValue / subPortfolioValue) * 100 : 0;
              const avgPriceForCost = stock.avgEntryPrice || stock.avgBuyPrice || 0;
              const costValueUSD = stock.costValue ?? (stock.quantity * avgPriceForCost);
              const costValueConverted = costValueUSD * exchangeRate;

              const isExpanded = expandedSymbol === stock.stockSymbol;
              const isSelected = selectedStocks.includes(stock.stockSymbol);
              return (
                <div
                  key={idx}
                  className={`p-4 rounded-lg border transition-all duration-300 ${isBulkMoveMode
                    ? (isSelected
                      ? 'border-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.5)] bg-gray-800/80'
                      : 'border-gray-800 hover:border-gray-600')
                    : 'border-gray-800'
                    }`}
                  style={{ backgroundColor: 'var(--bg-item)' }}
                  onClick={(e) => {
                    if (isBulkMoveMode) {
                      e.stopPropagation();
                      toggleStockSelection(stock.stockSymbol); // เลือก/ยกเลิกเลือก
                    } else {
                      toggleRowExpand(stock.stockSymbol, e); // กางแถวตามเดิมถ้าไม่ได้อยู่ในโหมด Bulk
                    }
                  }}
                >
                  {/* แถวหลัก (คลิกเพื่อกาง) */}
                  <div className="cursor-pointer" >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--text-color)' }}>
                          {symbolText(stock)}
                          <svg className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </div>
                        <div className="text-xs" style={{ color: 'var(--yellow-text-color)' }}>{qtyText(stock.quantity)} {unitLabel} @ {isCrypto ? formatCryptoUsd(num(stock.avgBuyPrice)) : (stock.avgBuyPrice * exchangeRate).toFixed(2)}</div>
                        <div className="text-[11px] mt-1 text-gray-400" style={{ color: 'var(--text-color)' }}>ต้นทุน: {currency === 'THB' ? '฿' : '$'}{money(costValueConverted, { minimumFractionDigits: 2 })}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div className="text-base font-bold" style={{ color: 'var(--text-color)' }}>{currency === 'THB' ? '฿' : '$'}{money(stockValue, { minimumFractionDigits: 2 })}</div>
                        <div className={`text-xs font-semibold ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{profit >= 0 ? '+' : '-'}{currency === 'THB' ? '฿' : '$'}{money(Math.abs(profit), { maximumFractionDigits: 0 })} ({profitPercent.toFixed(1)}%)</div>
                      </div>
                    </div>
                  </div>

                  {/* ส่วนรายละเอียด (กางออก) */}
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-700/50 grid grid-cols-2 gap-2 animate-fadeIn mb-3">
                      {!isCrypto && <div className="bg-black/20 p-2 rounded">

                        <div className="text-[10px] text-gray-400" style={{ color: 'var(--text-color)' }}>ปันผลสุทธิ
                          {!isPortAdmin && displayMode === 'V1' && (
                            <button
                              onClick={() => { setSelectedStockForGain(stock); setIsEditDividendOpen(true); }}
                              className="text-[10px] ml-1 bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white px-2 py-0.5 rounded transition-colors"
                            >
                              แก้ไข
                            </button>
                          )}</div>
                        <div className="text-xs font-bold text-blue-400" style={{ color: 'var(--blue-text-color)' }}>${stock.settledDividendNet?.toLocaleString() || stock.manualDividend?.toLocaleString() || ' -'}</div>
                      </div>}
                      <div className="bg-black/20 p-2 rounded relative">
                        <div className="flex justify-between items-center">
                          <div className="text-[10px] text-gray-400" style={{ color: 'var(--text-color)' }}>Realized Gain</div>
                          {!isPortAdmin && !isCrypto && (
                            <button onClick={() => { setSelectedStockForGain(stock); setIsEditGainOpen(true); }} className="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">แก้ไข</button>
                          )}
                        </div>
                        {(() => {
                          const rawGain = stock.realizedGain ?? stock.manualRealizedGain;
                          if (rawGain === undefined || rawGain === null) {
                            return <div className="text-xs font-bold" style={{ color: 'var(--text-color)' }}>$ -</div>;
                          }
                          const gain = Number(rawGain);
                          return (
                            <div className="text-xs font-bold" style={{ color: gain < 0 ? 'var(--red-text-color)' : 'var(--green-text-color)' }}>
                              {gain < 0 ? '-' : ''}${money(Math.abs(gain))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-gray-700 pt-3 relative">
                    <div className="flex-1 pr-4">
                      <div className="flex justify-between text-[12px] mb-1">
                        <span style={{
                          color: fromShayAdmin ? 'var(--text-color)' : getProgressBarColor(getTargetProgress(currentRatio, stock.expectedRatio), stock.expectedRatio)
                        }}>
                          {currentRatio.toFixed(1)}%
                        </span>
                        {!fromShayAdmin && (
                          <span style={{ color: 'var(--text-color)' }}>เป้าหมาย: {stock.expectedRatio}%</span>
                        )}
                      </div>
                      {!fromShayAdmin && (
                        <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                          <div className="h-full" style={{
                            width: `${Math.min(getTargetProgress(currentRatio, stock.expectedRatio), 100)}%`,
                            backgroundColor: getProgressBarColor(getTargetProgress(currentRatio, stock.expectedRatio), stock.expectedRatio)
                          }} />
                        </div>
                      )}
                    </div>

                    {!isPortAdmin && (
                      <div className="relative">
                        <button onClick={(e) => toggleMenu(stock.stockSymbol, e)} className="p-1.5 text-gray-400 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors focus:outline-none">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" /></svg>
                        </button>


                        {activeMenu === stock.stockSymbol && (
                          <div className="absolute right-0 bottom-10 w-32 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-[100] flex flex-col overflow-hidden text-left" onClick={(e) => e.stopPropagation()}>
                            <button onClick={(e) => { e.stopPropagation(); onEditStock?.(stock); setSelectedStock(stock); setActiveMenu(null); setIsEditStockOpen(true); }} className="px-4 py-2.5 text-sm font-bold text-blue-400 hover:bg-gray-700 text-left border-b border-gray-700/50">แก้ไข</button>
                            <button onClick={(e) => { e.stopPropagation(); onMoveStock?.(stock); setActiveMenu(null); }} className="px-4 py-2.5 text-sm font-bold text-yellow-400 hover:bg-gray-700 text-left border-b border-gray-700/50">ย้าย</button>
                            {displayMode === 'V1' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // เปลี่ยนจาก (stock) เป็น (stock.stockSymbol)
                                  handleDeleteClick?.(stock.stockSymbol);
                                  setActiveMenu(null);
                                }}
                                className="px-4 py-2.5 text-sm font-bold text-red-400 hover:bg-gray-700 text-left border-b border-gray-700/50"
                              >
                                ลบ
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (displayMode === 'V1') {
                                  setStockForPartialSell(stock);
                                  setIsSellAllMode(false);
                                  setIsPartialSellOpen(true);

                                  setActiveMenu(null);
                                } else {
                                  openTransactionModal({
                                    portId: id || portfolioId,
                                    subPortId: portfolio.subPortId,
                                    symbol: stock.stockSymbol,
                                    currentPrice: currentPrices[stock.stockSymbol] // ส่งราคาปัจจุบันไปด้วยถ้ามี
                                  });
                                }

                              }}
                              className="px-4 py-2.5 text-sm font-bold text-purple-400 hover:bg-gray-700 text-left border-b border-gray-700/50"
                            >
                              ขายบางส่วน
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (displayMode === 'V1') {
                                  setStockForPartialSell(stock);
                                  setIsSellAllMode(true);   // ✅ เปิดโหมดขายทั้งหมด
                                  setIsPartialSellOpen(true);
                                  setActiveMenu(null);
                                } else {
                                  // สำหรับ V2: เปิด Modal ซื้อขาย พร้อมพ่วงจำนวนหุ้นทั้งหมดไปเป็นค่าตั้งต้น
                                  // (ถ้าคุณอยากให้ผู้ใช้กรอกจำนวนเองด้วย ก็ไม่ต้องส่ง quantity ไปครับ แต่ตามโจทย์คือส่งจำนวนหุ้นทั้งหมดมา)
                                  openTransactionModal({
                                    portId: id || portfolioId,
                                    subPortId: portfolio.subPortId,
                                    symbol: stock.stockSymbol,
                                    // หมายเหตุ: หากต้องการส่งจำนวนหุ้นเริ่มต้นไป ให้ตรวจสอบว่า openTransactionModal รองรับ parameter เพิ่มเติมไหม 
                                    // หรือถ้าต้องการให้กรอกเองทั้งหมดตามโจทย์ สามารถใช้รูปแบบเดียวกับขายบางส่วนได้เลยครับ
                                    initialQuantity: stock.quantity, // ✅ ดึงจำนวนหุ้นทั้งหมดที่มีโยนเข้าไป
                                    isSellAll: true
                                  });
                                }


                              }}
                              className="px-4 py-2.5 text-sm font-bold text-orange-400 hover:bg-gray-700 text-left border-b border-gray-700/50"
                            >
                              ขายทั้งหมด
                            </button>

                            <button onClick={(e) => { e.stopPropagation(); setActiveMenu(null); localStorage.setItem('selectedSymbol', stock.stockSymbol); window.location.href = '/'; }} className="px-4 py-2.5 text-sm font-bold text-green-400 hover:bg-gray-700 text-left">📈 ไปยังกราฟ</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (

        <div className="flex flex-col items-center justify-center py-16 px-4 text-center animate-fadeIn">
          <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4 text-gray-500">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path></svg>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">ยังไม่มี{unitLabel}ในพอร์ตนี้</h3>

          {isV2 ? (
            <p className="text-gray-400 text-sm mb-8 max-w-xs">เริ่มต้นบันทึกข้อมูลการลงทุนของคุณผ่านหนึ่งในวิธีเหล่านี้</p>
          ) : (
            <p className="text-gray-400 text-sm mb-8 max-w-sm">เริ่มต้นบันทึกข้อมูลการลงทุนของคุณโดยการกด "+ เพิ่มหุ้น"</p>
          )}

          {isV2 && !fromShayAdmin && (
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
              {/* ปุ่ม Import (สลับเป็น Yellow) — พอร์ตคริปโตยังไม่เปิด Import CSV */}
              {!isCrypto && <div className="relative inline-block w-full flex-1">

                {/* 2. Tag ลอยอยู่ข้างนอกปุ่ม (ใช้ absolute ได้เลย) */}
                <div className="absolute -top-2 -right-2 flex gap-1 z-10">
                  <span className="bg-blue-600 text-white text-[9px] px-1.5 py-0.5 rounded font-bold animate-pulse">BETA</span>
                  <span className="bg-green-600 text-white text-[9px] px-1.5 py-0.5 rounded font-bold">RECOMMEND</span>
                </div>

                {/* 3. ปุ่ม Import */}
                <button
                  onClick={() => setIsImportModalOpen(true)}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold py-4 px-4 rounded-lg transition overflow-hidden group relative"
                >
                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 w-full h-full bg-white/20 translate-x-[-100%] group-hover:animate-[shimmer_1.5s_infinite] pointer-events-none"></div>

                  <div className="text-sm">นำเข้าจากไฟล์ (Import CSV)</div>
                  <div className="text-[10px] font-normal text-black/70 mt-1 px-2">เหมาะสำหรับการ Import จำนวนมากจาก Broker</div>
                </button>
              </div>}

              {/* ปุ่มซื้อขายปัจจุบัน (สลับเป็น Gray) */}
              <button
                onClick={() => openTransactionModal({ portId: id, subPortId: portfolio.subPortId })}
                className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-lg border border-gray-600 transition"
              >
                <div className="text-sm">ซื้อขายปัจจุบัน</div>
                <div className="text-[10px] font-normal text-gray-400 mt-1 px-2">เหมาะสำหรับการซื้อขายในปัจจุบัน (จำเป็นต้องมีเงินสดในพอร์ต)</div>
              </button>
            </div>
          )}
        </div>

      )}

      <SellPartialStockModal
        isOpen={isPartialSellOpen}
        onClose={() => {
          setIsPartialSellOpen(false);
          setStockForPartialSell(null);
          setIsSellAllMode(false);
        }}
        portfolioId={id || portfolioId || portfolio.portId || ''}
        subPortfolioId={portfolio.subPortId || ''}
        stock={stockForPartialSell}
        isSellAll={isSellAllMode} // ✅ ส่งค่าสถานะบอก Modal
        onSave={() => {
          if (onRefresh) onRefresh();
        }}
      />

      <EditRealizeGainModal
        isOpen={isEditGainOpen}
        onClose={() => { setIsEditGainOpen(false); setSelectedStockForGain(null); }}
        portfolioId={id || portfolioId || portfolio.portId || portfolio.portfolioId || portfolio.id || ''}
        stock={selectedStockForGain}
        subPortfolioId={portfolio.subPortId || portfolio.subPortfolioId || portfolio.id || ''}
        onSave={() => { if (onRefresh) onRefresh(); }}
      />

      {/* เรียกใช้งาน Modal */}
      <EditDividendModal
        isOpen={isEditDividendOpen}
        onClose={() => setIsEditDividendOpen(false)}
        portfolioId={id || portfolioId || portfolio.portId || portfolio.portfolioId || portfolio.id || ''}
        stock={selectedStockForGain}
        subPortfolioId={portfolio.subPortId || portfolio.subPortfolioId || portfolio.id || ''}
        onSave={() => { if (onRefresh) onRefresh(); }}
      />

      <AddEditStock
        isOpen={isEditStockOpen}
        onClose={() => { setIsEditStockOpen(false); setSelectedStock(null); setIsEditTargetOpen(false); }}
        portfolio={portfolio}
        initialData={selectedStock}
        onRefresh={onRefresh}
        portfolioId={id || portfolioId || portfolio.portId || portfolio.portfolioId || portfolio.id || ''}
        setIsConfigPopupOpen={(isOpen) => {
          setIsEditTargetOpen(isOpen); // สั่งเปิด modal ขวา
          // ถ้าต้องการให้ AddEditStock ปิดเมื่อเปิดตัวขวา ให้ใส่ onClose() ตรงนี้ด้วย
          // onClose(); 
        }}
        isAllocationOpen={isEditTargetOpen}
      />
      <EditHoldingAllocationModal
        isOpen={isEditTargetOpen}
        onClose={() => setIsEditTargetOpen(false)}
        portfolioId={id || portfolioId || portfolio.portId || portfolio.portfolioId || portfolio.id || ''}
        subPortfolio={portfolio}
        onSave={() => {
          if (onRefresh) onRefresh();
        }}
        isSideBySide={isEditStockOpen} // ✅ เพิ่ม prop นี้เพื่อเปิด Side-by-Side
      />
    </div>
  );
};

export default PortfolioStocksBox;